import React, {
  Fragment,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import makeStyles from "@mui/styles/makeStyles";
import { propEq, find, reject, equals } from "ramda";
import {
  Button,
  Paper,
  MenuList,
  MenuItem,
  Popper,
  Grow,
  ClickAwayListener,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const useStyles = makeStyles(theme => ({
  button: {
    width: "170px",
    height: "38px",
    border: `2px solid #127DA9`,
    borderRadius: "30px",
    justifyContent: "center",
  },
  label: {
    color: "#127DA9",
  },
  menuContainer: {
    zIndex: "1000",
  },
  menu: {
    borderRadius: "5px",
    boxShadow: "0px 0px 30px rgba(149, 169, 177, 0.3)",
    width: "170px",
  },
  menuItem: {
    transition: "none",
    "&:hover": {
      backgroundColor: "#0D5876",
      color: "white",
    },
  },
}));

const filters = [
  {
    id: 1,
    buttonText: "All Modules",
    sort: { property: "name", order: "asc" },
    otherParam: { status: "ACTIVE,INACTIVE" },
  },
  {
    id: 2,
    buttonText: "Published",
    sort: { property: "name", order: "asc" },
    otherParam: { status: "ACTIVE" },
  },
];

const ModuleFilters = props => {
  const classes = useStyles();
  const { onChangeFilter, handleUpdateSort } = props;

  //-- Handle drop down menu state
  const [selectedId, setSelectedId] = useState(1);

  const currentFilter = useCallback(item => equals(item.id, selectedId), [
    selectedId,
  ]);
  const [filterOptions, setfilterOptions] = useState(
    reject(currentFilter, filters),
  );

  useEffect(() => {
    const filteredOptions = reject(currentFilter, filters);
    setfilterOptions(filteredOptions);
  }, [selectedId, currentFilter]);

  const anchorRef = useRef(null);
  const [openMenu, setOpenMenu] = useState(false);
  const toggleMenu = () => setOpenMenu(!openMenu);
  const closeMenu = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpenMenu(false);
  };

  const handleFilterChange = ({ sort, otherParam, id }) => event => {
    handleUpdateSort(sort);
    onChangeFilter({ status: otherParam.status });
    setSelectedId(id);
    closeMenu(event);
  };

  const handleListKeyDown = event => {
    if (equals(event.key, "Tab")) {
      event.preventDefault();
      setOpenMenu(false);
    }
  };

  const prevOpen = useRef(openMenu);
  useEffect(() => {
    if (prevOpen.current && !openMenu) {
      anchorRef.current.focus();
    }
    prevOpen.current = openMenu;
  }, [openMenu]);

  return (
    <Fragment>
      <Button
        ref={anchorRef}
        onClick={toggleMenu}
        classes={{ root: classes.button, label: classes.label }}
        endIcon={<ExpandMoreIcon />}
        aria-controls={openMenu && "menu-list-grow"}
        aria-haspopup="true"
      >
        {find(propEq("id", selectedId), filters).buttonText}
      </Button>
      <Popper
        className={classes.menuContainer}
        open={openMenu}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper classes={{ root: classes.menu }}>
              <ClickAwayListener onClickAway={closeMenu}>
                <MenuList
                  autoFocusItem={openMenu}
                  id="menu-list-grow"
                  onKeyDown={handleListKeyDown}
                >
                  {filterOptions.map(filter => (
                    <MenuItem
                      key={filter.id}
                      classes={{ root: classes.menuItem }}
                      onClick={handleFilterChange(filter)}
                      disabled={filter.buttonText === "Approved"}
                    >
                      {filter.buttonText}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Fragment>
  );
};

export default ModuleFilters;
